import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import SectionLoader from 'components/shared/SectionLoader';
import { FieldArray, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { Service } from './types';

const Services = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));

  const { values, handleChange, handleBlur, setFieldValue } = useFormikContext<any>();
  const { dealType, client } = values;
  const [services, setServices] = useState<Service[]>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dealType?.dealTypeId && (client?.id || client?.accountId)) return;

    const fetchServices = async () => {
      try {
        setLoading(true);
        const data = await DealsService.getServicesByDealTypeIdAndClientId(
          dealType.dealTypeId,
          client.id ?? client?.accountId,
        );
        setServices(data.services);
      } catch (error) {
        console.error('No such services for the required deal type');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [dealType?.dealTypeId, client?.id, client?.accountId]);

  return (
    <FieldArray name='services'>
      {({ push, remove }) => (
        <>
          {values?.services?.map((item: Service, index: number) => (
            <Box
              key={`services.${index}.serviceId`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '24px',
              }}
            >
              {index < values?.services?.length && index !== 0 && (
                <Divider sx={{ borderColor: zeplinColor.Neutral6 }} />
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                }}
              >
                <FormControl fullWidth sx={{ width: '100%', minWidth: 0 }}>
                  <InputLabel
                    size={isXs ? 'small' : 'medium'}
                    sx={{
                      fontSize: isXs ? 14 : 16,
                      '& .MuiFormLabel-asterisk': {
                        color: zeplinColor.Error,
                      },
                    }}
                    required
                  >
                    Select Service
                  </InputLabel>
                  <Select
                    disabled={!dealType?.dealTypeId}
                    IconComponent={KeyboardArrowDown}
                    name={`services.${index}.serviceId`}
                    value={item.serviceId}
                    label={'Select Service'}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isXs ? 'small' : 'medium'}
                    sx={{
                      width: '100%',
                      minWidth: 0,
                      '& .MuiSelect-select': {
                        fontSize: isXs ? 14 : 16,
                      },
                    }}
                    endAdornment={
                      item.serviceId && (
                        <IconButton
                          size='small'
                          sx={{
                            marginRight: 2,
                            visibility: item.serviceId ? 'visible' : 'hidden',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChange({
                              target: { name: `services.${index}.serviceId`, value: '' },
                            });
                          }}
                        >
                          <ClearIcon fontSize='small' />
                        </IconButton>
                      )
                    }
                  >
                    {loading ? (
                      <SectionLoader />
                    ) : (
                      services?.length &&
                      services.map((opt) => (
                        <MenuItem key={opt.serviceId} value={opt.serviceId}>
                          {opt.serviceName}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Box
                  sx={{
                    display: 'inline-flex',
                    justifyContent: 'space-between',
                    gap: '24px',
                  }}
                >
                  {[
                    {
                      id: 'floorRate',
                      label: 'Floor',
                      value:
                        services?.find((service) => service.serviceId === item.serviceId)
                          ?.floorRate ?? 0,
                    },
                    { id: 'targetPrice', label: 'Target', value: item.targetPrice },
                    {
                      id: 'ceilingRate',
                      label: 'Ceiling',
                      value:
                        services?.find((service) => service.serviceId === item.serviceId)
                          ?.ceilingRate ?? 0,
                    },
                  ].map(({ id, label, value }) => (
                    <NumericFormat
                      required={id === 'targetPrice'}
                      customInput={TextField}
                      onBlur={handleBlur}
                      sx={{
                        flex: 1,
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& .MuiFormLabel-asterisk': {
                          color: zeplinColor.Error,
                        },
                      }}
                      slotProps={{
                        input: {
                          readOnly: id === 'floorRate' || id === 'ceilingRate',
                          sx: { fontSize: isXs ? 14 : 16 },
                        },
                        inputLabel: {
                          size: isXs ? 'small' : 'medium',
                          sx: { fontSize: isXs ? 14 : 16 },
                        },
                      }}
                      size={isXs ? 'small' : 'medium'}
                      value={item.serviceId && value >= 0 ? String(value) : ''}
                      label={`${label} Price`}
                      name={`services.${index}.${id}`}
                      onValueChange={(values) => {
                        setFieldValue(`services.${index}.${id}`, values.floatValue);
                      }}
                      prefix='$'
                      thousandSeparator
                    />
                  ))}
                </Box>
              </Box>

              {index !== 0 && (
                <CustomButton
                  disabled={false}
                  variant='text'
                  text={'Remove Service'}
                  style={{
                    alignSelf: 'flex-start',
                  }}
                  onClick={() => remove(index)}
                />
              )}
            </Box>
          ))}
          <CustomButton
            disabled={false}
            variant='text'
            text={'Add Service'}
            onClick={() => push({ serviceId: '', targetPrice: 0 })}
          />
        </>
      )}
    </FieldArray>
  );
};

export default Services;
